import React from 'react';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { RackSample } from '@/api/dashboardSummary';

// Unified status-green token. ONE green across every dot/circle on the page
// (was split between bg-success #16a34a and bg-emerald-500 #10b981). `bg-success`
// is theme-aware (light #16a34a / dark #22c55e) so it stays coherent in dark mode.
export const STATUS_GREEN = 'bg-success';

// One overall-state colour scale for the header status circles + the per-row
// dots. Keyed by a small semantic enum so each card maps its own data onto it.
export type OverallTone = 'ok' | 'attention' | 'critical' | 'none';
export const TONE_DOT: Record<OverallTone, string> = {
  ok: STATUS_GREEN,
  attention: 'bg-amber-500',
  critical: 'bg-red-500',
  none: 'bg-muted-foreground/50',
};

// Colours mirror the Telegram alert emojis so operators read one scale
// everywhere: 🟡 частично → true yellow (amber read as orange), 🟠 заметно
// меньше → orange, почти пустая → near-black (dark grey in dark mode so the
// dot stays visible on a dark card).
export const SAMPLE_DOT: Record<RackSample['status'] | 'red', string> = {
  grey: 'bg-zinc-800 dark:bg-zinc-300',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  yellow: 'bg-yellow-400',
  green: STATUS_GREEN,
  unknown: 'bg-slate-400',
};

// Small status circle pinned by a card header, carrying its overall-state text
// in a hover/focus tooltip — replaces the wordy per-card text captions.
// `dotClass` overrides the tone's colour with the WORST inner dot's colour so
// the header circle never disagrees with the per-unit dots (e.g. an amber
// "attention" circle over rows that are all yellow).
export function HeaderStatusDot({ tone, label, dotClass }: { tone: OverallTone; label: string; dotClass?: string }) {
  return (
    <Tooltip content={label} side="top">
      <span
        tabIndex={0}
        role="img"
        aria-label={label}
        className={cn(
          'inline-block size-2.5 shrink-0 rounded-full outline-none ring-offset-2 ring-offset-card focus-visible:ring-2 focus-visible:ring-ring',
          dotClass ?? TONE_DOT[tone],
          tone === 'critical' && 'animate-pulse',
        )}
      />
    </Tooltip>
  );
}
